import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { tokens } from "../../theme";
import {
    Box, Typography, useTheme, Button, ButtonGroup, Step, StepLabel, Stepper, Paper,
    CircularProgress, Autocomplete, TextField, Chip, Checkbox, Table,
    TableHead, TableBody, TableRow, TableCell, Alert, FormControl,
    InputLabel, Select, MenuItem, LinearProgress, Tooltip,
    Dialog, DialogTitle, DialogContent, IconButton, Divider,
} from "@mui/material";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import SortIcon from '@mui/icons-material/Sort';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import QuestionRender from '../../components/QuestionRender';
import api from '../../utils/v2Api';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

const STEPS = ['Select Course', 'Select Pages & Tags', 'Select Questions', 'Configure & Create'];

const SEASON_MAP = { 1: 'Spring', 2: 'Summer', 3: 'Fall', 4: 'Winter' };
const getSeasonName = (s) => SEASON_MAP[s] || '';

const GenerateTestWizard = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState(null);

    // Step 1 state
    const [courses, setCourses] = useState([]);
    const [coursesLoading, setCoursesLoading] = useState(false);
    const [selectedSemester, setSelectedSemester] = useState('');
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [coveredData, setCoveredData] = useState(null);
    const [coveredLoading, setCoveredLoading] = useState(false);

    // Step 2 state
    const [selectedTextbook, setSelectedTextbook] = useState(null);
    const [pageFrom, setPageFrom] = useState('');
    const [pageTo, setPageTo] = useState('');
    const [matchPreview, setMatchPreview] = useState(null);
    const [matchLoading, setMatchLoading] = useState(false);

    // Step 3 state
    const [matchedQuestions, setMatchedQuestions] = useState([]);
    const [selectedQIds, setSelectedQIds] = useState(new Set());
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [questionTypeFilter, setQuestionTypeFilter] = useState('');
    const [autoCount, setAutoCount] = useState(25);
    const [autoSelectMode, setAutoSelectMode] = useState('equal'); // 'equal' | 'equal-random' | 'random'

    // Step 4 state
    const [testTitle, setTestTitle] = useState('');
    const [splitSections, setSplitSections] = useState(false);
    const [creating, setCreating] = useState(false);

    // Ordering state
    const [sortOrder, setSortOrder] = useState('page'); // 'page' | 'random'
    const [shuffleSeed, setShuffleSeed] = useState(0);

    // Preview modal
    const [previewOpen, setPreviewOpen] = useState(false);

    // Fetch courses on mount
    useEffect(() => {
        const fetchCourses = async () => {
            setCoursesLoading(true);
            try {
                const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses`, {
                    headers: { Authorization: getAuthHeader() },
                });
                setCourses(res.data || []);
            } catch (err) {
                console.error('Failed to fetch courses:', err);
            }
            setCoursesLoading(false);
        };
        fetchCourses();
    }, []);

    // Derive unique semesters from courses (sorted newest first)
    const semesters = React.useMemo(() => {
        const map = new Map();
        courses.forEach((c) => {
            (c.course_semesters || []).forEach((cs) => {
                const sem = cs.semester;
                if (sem && !map.has(sem.id)) {
                    map.set(sem.id, sem);
                }
            });
        });
        return [...map.values()].sort((a, b) => b.year - a.year || b.season - a.season);
    }, [courses]);

    // Filter courses by selected semester
    const filteredCourses = React.useMemo(() => {
        if (!selectedSemester) return courses;
        return courses.filter((c) =>
            (c.course_semesters || []).some((cs) => cs.semester?.id === selectedSemester)
        );
    }, [courses, selectedSemester]);

    // Fetch covered pages when course changes
    useEffect(() => {
        if (!selectedCourse) { setCoveredData(null); return; }
        const fetchCovered = async () => {
            setCoveredLoading(true);
            setError(null);
            try {
                const res = await api.get(`/courses/${selectedCourse.id}/covered-pages`);
                setCoveredData(res.data);
                // Auto-select first textbook
                if (res.data.textbooks?.length > 0) {
                    const tb = res.data.textbooks[0];
                    setSelectedTextbook(tb);
                    if (tb.covered_ranges?.length > 0) {
                        setPageFrom(String(tb.covered_ranges[0][0]));
                        setPageTo(String(tb.covered_ranges[tb.covered_ranges.length - 1][1]));
                    }
                } else {
                    setSelectedTextbook(null);
                }
            } catch (err) {
                setError('Failed to load course coverage data');
            }
            setCoveredLoading(false);
        };
        fetchCovered();
    }, [selectedCourse]);

    // Fetch match preview when page range changes (debounced)
    useEffect(() => {
        if (!selectedTextbook || !pageFrom || !pageTo) { setMatchPreview(null); return; }
        const timer = setTimeout(async () => {
            setMatchLoading(true);
            try {
                const res = await api.get('/question-bank/match-by-pages', {
                    textbook_id: selectedTextbook.textbook_id,
                    page_from: parseInt(pageFrom),
                    page_to: parseInt(pageTo),
                    limit: 500,
                });
                setMatchPreview(res.data);
            } catch (err) {
                console.error('Match preview failed:', err);
            }
            setMatchLoading(false);
        }, 400);
        return () => clearTimeout(timer);
    }, [selectedTextbook, pageFrom, pageTo]);

    // When moving to step 3, load full questions
    const loadMatchedQuestions = useCallback(async () => {
        if (!selectedTextbook || !pageFrom || !pageTo) return;
        setQuestionsLoading(true);
        try {
            const res = await api.get('/question-bank/match-by-pages', {
                textbook_id: selectedTextbook.textbook_id,
                page_from: parseInt(pageFrom),
                page_to: parseInt(pageTo),
                question_type: questionTypeFilter || undefined,
                limit: 500,
            });
            setMatchedQuestions(res.data.data || []);
            // Auto-select top N
            autoSelectQuestions(res.data.data || [], autoCount);
        } catch (err) {
            console.error('Failed to load questions:', err);
        }
        setQuestionsLoading(false);
    }, [selectedTextbook, pageFrom, pageTo, questionTypeFilter]);

    const autoSelectQuestions = (questions, count, mode = autoSelectMode) => {
        const shuffle = (arr) => {
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            return arr;
        };
        if (mode === 'random') {
            setSelectedQIds(new Set(shuffle([...questions]).slice(0, count).map(q => q.id)));
            return;
        }
        // Equal distribution across tags (round-robin with overflow redistribution)
        const tagMap = new Map(); // tag -> [questions]
        for (const q of questions) {
            for (const tag of (q.matching_tags || [])) {
                if (!tagMap.has(tag)) tagMap.set(tag, []);
                tagMap.get(tag).push(q);
            }
        }
        const tags = [...tagMap.keys()];
        if (tags.length === 0) {
            setSelectedQIds(new Set(questions.slice(0, count).map(q => q.id)));
            return;
        }
        const ids = new Set();
        let remaining = count;
        let activeTags = tags.map(tag => {
            const tagQuestions = [...tagMap.get(tag)];
            return {
                tag,
                questions: mode === 'equal-random'
                    ? shuffle(tagQuestions)
                    : tagQuestions.sort((a, b) => b.match_score - a.match_score),
                idx: 0,
            };
        });
        while (remaining > 0 && activeTags.length > 0) {
            const perTag = Math.max(1, Math.floor(remaining / activeTags.length));
            const sizeBefore = ids.size;
            const nextActive = [];
            for (const entry of activeTags) {
                let picked = 0;
                while (picked < perTag && entry.idx < entry.questions.length && remaining > 0) {
                    const q = entry.questions[entry.idx];
                    entry.idx++;
                    if (!ids.has(q.id)) {
                        ids.add(q.id);
                        picked++;
                        remaining--;
                    }
                }
                if (entry.idx < entry.questions.length) {
                    nextActive.push(entry);
                }
            }
            if (ids.size === sizeBefore) break; // no progress, avoid infinite loop
            activeTags = nextActive;
        }
        setSelectedQIds(ids);
    };

    const handleNext = () => {
        if (activeStep === 1) {
            loadMatchedQuestions();
        }
        if (activeStep === 2 && !testTitle) {
            const courseName = selectedCourse?.course_title || 'Test';
            setTestTitle(`${courseName} - Pages ${pageFrom}-${pageTo}`);
        }
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const toggleQuestion = (id) => {
        setSelectedQIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        const visible = sortedQuestions;
        if (visible.every(q => selectedQIds.has(q.id))) {
            setSelectedQIds(prev => {
                const next = new Set(prev);
                visible.forEach(q => next.delete(q.id));
                return next;
            });
        } else {
            setSelectedQIds(prev => {
                const next = new Set(prev);
                visible.forEach(q => next.add(q.id));
                return next;
            });
        }
    };

    const getFilteredQuestions = () => {
        if (!questionTypeFilter) return matchedQuestions;
        return matchedQuestions.filter(q => q.question_type === questionTypeFilter);
    };

    const sortedQuestions = useMemo(() => {
        const questions = questionTypeFilter
            ? matchedQuestions.filter(q => q.question_type === questionTypeFilter)
            : [...matchedQuestions];
        if (sortOrder === 'random') {
            const arr = [...questions];
            let seed = shuffleSeed || 1;
            const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(rand() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            return arr;
        }
        return [...questions].sort((a, b) => {
            const pa = a.min_page ?? Infinity;
            const pb = b.min_page ?? Infinity;
            if (pa !== pb) return pa - pb;
            return b.match_score - a.match_score;
        });
    }, [matchedQuestions, questionTypeFilter, sortOrder, shuffleSeed]);

    const handleCreate = async () => {
        setCreating(true);
        setError(null);
        try {
            const selected = sortedQuestions.filter(q => selectedQIds.has(q.id));
            const mcqs = selected.filter(q => q.question_type === 'mcq');
            const frqs = selected.filter(q => q.question_type !== 'mcq');

            let sections;
            if (splitSections && mcqs.length > 0 && frqs.length > 0) {
                sections = [
                    buildSection('Multiple Choice', 1, 'mcq', mcqs),
                    buildSection('Free Response', 2, 'frq', frqs),
                ];
            } else {
                sections = [buildSection('Section 1', 1, 'mixed', selected)];
            }

            const res = await api.post('/tests', {
                title: testTitle,
                use_question_ui: true,
                metadata: {
                    generated_from: {
                        textbook_id: selectedTextbook.textbook_id,
                        page_from: parseInt(pageFrom),
                        page_to: parseInt(pageTo),
                        course_id: selectedCourse?.id,
                    },
                },
                sections,
            });

            navigate(`/admin/tests-v2/${res.data.data.id}/edit`);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create test');
            setCreating(false);
        }
    };

    const buildSection = (name, number, type, questions) => ({
        section_name: name,
        section_number: number,
        section_type: type,
        questions: questions.map((q, i) => ({
            question_number: i + 1,
            question_type: q.question_type,
            question_prompt: q.question_prompt,
            answer: q.answer,
            is_multi_select: q.is_multi_select || false,
            max_points: q.question_type === 'mcq' ? 1 : (q.v2_bank_question_parts?.reduce((s, p) => s + (p.max_points || 0), 0) || 1),
            accepted_answers: q.accepted_answers,
            metadata: { source_bank_question_id: q.id },
            options: (q.v2_bank_question_options || []).map(o => ({
                option_label: o.option_label,
                option_text: o.option_text,
                order: o.order,
            })),
            parts: (q.v2_bank_question_parts || []).map(p => ({
                part_label: p.part_label,
                part_order: p.part_order,
                criteria_name: p.criteria_name,
                max_points: p.max_points,
                rubric_text: p.rubric_text,
                rubric_template_id: p.rubric_template_id,
                metadata: p.metadata,
            })),
        })),
    });

    const isStepValid = () => {
        switch (activeStep) {
            case 0: return !!selectedCourse && !!coveredData;
            case 1: return !!selectedTextbook && !!pageFrom && !!pageTo && matchPreview?.total > 0;
            case 2: return selectedQIds.size > 0;
            case 3: return !!testTitle;
            default: return false;
        }
    };

    // Coverage bar for step 2
    const CoverageBar = ({ textbook }) => {
        if (!textbook?.total_pages || !textbook?.covered_ranges) return null;
        const total = textbook.total_pages;
        return (
            <Box sx={{ mt: 1, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    Coverage: {textbook.covered_pages_count}/{total} pages ({textbook.coverage_percent}%)
                </Typography>
                <Box sx={{ position: 'relative', height: 12, borderRadius: 1, overflow: 'hidden', bgcolor: colors.primary[700], mt: 0.5 }}>
                    {textbook.covered_ranges.map(([from, to], i) => (
                        <Tooltip key={i} title={`Pages ${from}–${to}`} arrow placement="top">
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${((from - 1) / total) * 100}%`,
                                    width: `${((to - from + 1) / total) * 100}%`,
                                    height: '100%',
                                    bgcolor: colors.greenAccent[600],
                                    cursor: 'pointer',
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        );
    };

    // ─── Step renderers ───

    const renderStep1 = () => (
        <Box>
            <FormControl sx={{ mb: 2, minWidth: 200 }}>
                <InputLabel>Semester</InputLabel>
                <Select
                    value={selectedSemester}
                    label="Semester"
                    onChange={(e) => {
                        setSelectedSemester(e.target.value);
                        setSelectedCourse(null);
                        setCoveredData(null);
                    }}
                >
                    <MenuItem value="">All Semesters</MenuItem>
                    {semesters.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                            {getSeasonName(s.season)} {s.year}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Autocomplete
                options={filteredCourses}
                getOptionLabel={(c) => c.code ? `${c.code} — ${c.course_title}` : c.course_title || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, c) => {
                    const sem = c.course_semesters?.[0]?.semester;
                    return (
                        <li {...props} key={c.id}>
                            <Box>
                                <Typography variant="body1">
                                    {c.code ? `${c.code} — ` : ''}{c.course_title}
                                </Typography>
                                {sem && (
                                    <Typography variant="caption" color="text.secondary">
                                        {getSeasonName(sem.season)} {sem.year}
                                    </Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                value={selectedCourse}
                onChange={(_, v) => setSelectedCourse(v)}
                loading={coursesLoading}
                renderInput={(params) => <TextField {...params} label="Select Course" placeholder="Search by course name or code..." />}
                sx={{ mb: 3, maxWidth: 600 }}
            />
            {coveredLoading && <CircularProgress size={24} />}
            {coveredData && (
                <Paper sx={{ p: 2, bgcolor: colors.primary[600] }}>
                    <Typography variant="subtitle1" fontWeight="bold">{coveredData.course?.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Subject: {coveredData.course?.subject || 'N/A'} &bull; Events: {coveredData.events_count}
                    </Typography>
                    {coveredData.textbooks?.length > 0 ? (
                        coveredData.textbooks.map((tb) => (
                            <Box key={tb.textbook_id} sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    Textbook: <strong>{tb.title}</strong> ({tb.total_pages || '?'} pages)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Covered: {tb.covered_pages_count} pages ({tb.coverage_percent}%) &mdash;
                                    Ranges: {tb.covered_ranges.map(([a, b]) => `${a}-${b}`).join(', ') || 'None'}
                                </Typography>
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No textbooks linked to this course via class materials.
                        </Typography>
                    )}
                </Paper>
            )}
        </Box>
    );

    const renderStep2 = () => (
        <Box>
            {coveredData?.textbooks?.length > 1 && (
                <Autocomplete
                    options={coveredData.textbooks}
                    getOptionLabel={(tb) => tb.title}
                    value={selectedTextbook}
                    onChange={(_, v) => setSelectedTextbook(v)}
                    renderInput={(params) => <TextField {...params} label="Textbook" />}
                    sx={{ mb: 2, maxWidth: 400 }}
                />
            )}
            {selectedTextbook && (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {selectedTextbook.title} ({selectedTextbook.total_pages} pages)
                    </Typography>
                    <CoverageBar textbook={selectedTextbook} />

                    <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
                        <TextField
                            label="Page from"
                            type="number"
                            value={pageFrom}
                            onChange={(e) => setPageFrom(e.target.value)}
                            size="small"
                            sx={{ width: 120 }}
                        />
                        <Typography>to</Typography>
                        <TextField
                            label="Page to"
                            type="number"
                            value={pageTo}
                            onChange={(e) => setPageTo(e.target.value)}
                            size="small"
                            sx={{ width: 120 }}
                        />
                    </Box>

                    {matchLoading && <LinearProgress sx={{ mb: 2 }} />}

                    {matchPreview && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Topics found in selected pages:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                                {matchPreview.page_tags.map((t) => (
                                    <Chip key={t.id} label={t.name} size="small" color="primary" variant="outlined" />
                                ))}
                            </Box>
                            <Typography variant="body1" fontWeight="bold">
                                Matching questions available: {matchPreview.total}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );

    const renderStep3 = () => {
        const filtered = sortedQuestions;
        const allSelected = filtered.length > 0 && filtered.every(q => selectedQIds.has(q.id));

        return (
            <Box>
                <Box display="flex" gap={2} alignItems="center" sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Question Type</InputLabel>
                        <Select
                            value={questionTypeFilter}
                            onChange={(e) => setQuestionTypeFilter(e.target.value)}
                            label="Question Type"
                        >
                            <MenuItem value="">All Types</MenuItem>
                            <MenuItem value="mcq">MCQ</MenuItem>
                            <MenuItem value="saq">SAQ/FRQ</MenuItem>
                            <MenuItem value="sa">Short Answer</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Auto-select N"
                        type="number"
                        size="small"
                        value={autoCount}
                        onChange={(e) => setAutoCount(parseInt(e.target.value) || 0)}
                        sx={{ width: 120 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Mode</InputLabel>
                        <Select
                            value={autoSelectMode}
                            onChange={(e) => setAutoSelectMode(e.target.value)}
                            label="Mode"
                        >
                            <MenuItem value="equal">Equal per tag</MenuItem>
                            <MenuItem value="equal-random">Equal per tag (random)</MenuItem>
                            <MenuItem value="random">Fully random</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ShuffleIcon />}
                        onClick={() => autoSelectQuestions(filtered, autoCount)}
                    >
                        Auto-select
                    </Button>

                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Selected: {selectedQIds.size} / {matchedQuestions.length}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => setPreviewOpen(true)}
                            disabled={selectedQIds.size === 0}
                        >
                            Preview
                        </Button>
                    </Box>
                </Box>

                {questionsLoading ? (
                    <CircularProgress />
                ) : (
                    <Table size="small" sx={{ '& td, & th': { py: 0.5 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox checked={allSelected} onChange={toggleAll} />
                                </TableCell>
                                <TableCell>#</TableCell>
                                <TableCell>Page</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Score</TableCell>
                                <TableCell>Tags</TableCell>
                                <TableCell>Question</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((q, idx) => (
                                <TableRow key={q.id} hover sx={{ cursor: 'pointer' }} onClick={() => toggleQuestion(q.id)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={selectedQIds.has(q.id)} />
                                    </TableCell>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>
                                        {q.min_page != null
                                            ? (q.max_page != null && q.max_page !== q.min_page ? `${q.min_page}–${q.max_page}` : q.min_page)
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={q.question_type.toUpperCase()} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{q.match_score}</TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                            {(q.matching_tags || []).slice(0, 3).map(t => (
                                                <Chip key={t} label={t} size="small" sx={{ fontSize: '0.7rem' }} />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {(q.question_prompt || '').replace(/\{#.*?\}/g, '').substring(0, 120)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        );
    };

    const renderStep4 = () => {
        const selected = matchedQuestions.filter(q => selectedQIds.has(q.id));
        const mcqCount = selected.filter(q => q.question_type === 'mcq').length;
        const frqCount = selected.length - mcqCount;

        return (
            <Box>
                <TextField
                    label="Test Title"
                    value={testTitle}
                    onChange={(e) => setTestTitle(e.target.value)}
                    fullWidth
                    sx={{ mb: 2, maxWidth: 600 }}
                />

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Question Order</Typography>
                    <ButtonGroup size="small">
                        <Button
                            variant={sortOrder === 'page' ? 'contained' : 'outlined'}
                            onClick={() => setSortOrder('page')}
                            startIcon={<SortIcon />}
                        >
                            Page Order
                        </Button>
                        <Button
                            variant={sortOrder === 'random' ? 'contained' : 'outlined'}
                            onClick={() => { setSortOrder('random'); setShuffleSeed(s => s + 1); }}
                            startIcon={<ShuffleIcon />}
                        >
                            Shuffle
                        </Button>
                    </ButtonGroup>
                </Box>

                {mcqCount > 0 && frqCount > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant={splitSections ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => setSplitSections(!splitSections)}
                        >
                            {splitSections ? 'Split into MCQ + FRQ sections' : 'Single section (mixed)'}
                        </Button>
                    </Box>
                )}

                <Paper sx={{ p: 2, bgcolor: colors.primary[600], mb: 2 }}>
                    <Typography variant="subtitle2">Summary</Typography>
                    <Typography variant="body2">
                        {selected.length} questions ({mcqCount} MCQ, {frqCount} FRQ)
                    </Typography>
                    <Typography variant="body2">
                        Source: {selectedTextbook?.title}, Pages {pageFrom}-{pageTo}
                    </Typography>
                    <Typography variant="body2">
                        Course: {selectedCourse?.course_title}
                    </Typography>
                    {matchPreview?.page_tags && (
                        <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
                            {matchPreview.page_tags.map(t => (
                                <Chip key={t.id} label={t.name} size="small" />
                            ))}
                        </Box>
                    )}
                </Paper>

                <Button
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setPreviewOpen(true)}
                    disabled={selectedQIds.size === 0}
                >
                    Preview Final Order ({selectedQIds.size} questions)
                </Button>
            </Box>
        );
    };

    const stepContent = [renderStep1, renderStep2, renderStep3, renderStep4];

    return (
        <Box m="20px" pb="80px">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Header title="Generate Test" subtitle="Create a test from tagged textbook pages" />
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/tests-v2')}
                >
                    Back to Library
                </Button>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ p: 3, minHeight: 300 }}>
                {stepContent[activeStep]()}
            </Paper>

            <Box display="flex" justifyContent="space-between" mt={2}>
                <Button disabled={activeStep === 0} onClick={handleBack}>
                    Back
                </Button>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" onClick={() => navigate('/admin/tests-v2')}>
                        Cancel
                    </Button>
                    {activeStep < STEPS.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isStepValid()}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={creating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                            onClick={handleCreate}
                            disabled={!isStepValid() || creating}
                        >
                            Create Test
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Preview Selected Questions Modal */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { height: '80vh', overflow: 'hidden' } }}
            >
                <DialogTitle sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper',
                    borderBottom: 1, borderColor: 'divider',
                }}>
                    <Typography variant="h6">
                        Selected Questions ({selectedQIds.size})
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                            label="Jump to #"
                            type="number"
                            size="small"
                            sx={{ width: 100 }}
                            inputProps={{ min: 1, max: selectedQIds.size }}
                            onChange={(e) => {
                                const num = parseInt(e.target.value);
                                if (num >= 1) {
                                    const el = document.getElementById(`preview-q-${num}`);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }}
                        />
                        <IconButton onClick={() => setPreviewOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto' }}>
                    {sortedQuestions.filter(q => selectedQIds.has(q.id)).map((q, idx) => (
                        <Box key={q.id} id={`preview-q-${idx + 1}`} sx={{ mb: 3 }}>
                            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    Q{idx + 1}.
                                </Typography>
                                <Chip label={q.question_type.toUpperCase()} size="small" variant="outlined" />
                                {q.min_page != null && (
                                    <Chip
                                        label={q.max_page != null && q.max_page !== q.min_page ? `p.${q.min_page}–${q.max_page}` : `p.${q.min_page}`}
                                        size="small" color="primary" variant="outlined"
                                    />
                                )}
                                {(q.matching_tags || []).map(t => (
                                    <Chip key={t} label={t} size="small" sx={{ fontSize: '0.7rem' }} />
                                ))}
                            </Box>
                            {q.metadata && (
                                <Box display="flex" gap={2} sx={{ mb: 1 }}>
                                    {q.metadata.source_exam && (
                                        <Typography variant="caption" color="text.secondary">
                                            Source: {q.metadata.source_exam}
                                        </Typography>
                                    )}
                                    {q.metadata.source_semester && (
                                        <Typography variant="caption" color="text.secondary">
                                            Semester: {q.metadata.source_semester}
                                        </Typography>
                                    )}
                                    {q.metadata.difficulty && (
                                        <Typography variant="caption" color="text.secondary">
                                            Difficulty: {q.metadata.difficulty}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <QuestionRender
                                content={(q.question_prompt || '').replace(/\{#.*?\}/g, '')}
                                sx={{ mb: 1 }}
                            />
                            {q.v2_bank_question_options?.length > 0 && (
                                <Box sx={{ pl: 2 }}>
                                    {q.v2_bank_question_options.map(o => (
                                        <Box key={o.option_label} display="flex" gap={0.5} sx={{ mb: 0.5 }}>
                                            <Typography variant="body2" fontWeight="bold">{o.option_label}.</Typography>
                                            <QuestionRender content={o.option_text || ''} />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                    Answer:
                                </Typography>
                                {q.answer && (
                                    <QuestionRender content={q.answer} sx={{ display: 'inline', ml: 0.5 }} />
                                )}
                                {q.accepted_answers?.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        (Also accepted: {q.accepted_answers.join(', ')})
                                    </Typography>
                                )}
                            </Box>
                            {idx < selectedQIds.size - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default GenerateTestWizard;
